import React, { useEffect } from 'react';
import { FileText, Shield, User, Lock, Database, Scale, Globe, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/buttons/BackButton';
import HeaderNoAuth from '../components/Header/HeaderNoAuth';
import Card from '../components/Card/Card';
import SectionHeader from '../components/SectionHeader/SectionHeader';
import Button from '../components/buttons/Button';
import { openExternalUrl } from '../utils/browserHelper';
import './TermsOfService.css';

function TermsOfService() {
  const { isSubscribed, profile } = useAuth();
  const navigate = useNavigate();
  const isAuthenticated = isSubscribed || profile;
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: 'Les présentes Conditions Générales d\'Utilisation régissent votre accès et utilisation de l\'application voxia. Voxia est un outil d\'assistance administrative destiné à faciliter la rédaction de documents médicaux. Voxia n\'est pas un dispositif médical et ne fournit aucun conseil médical, diagnostic ou traitement.'
    },
    {
      id: 'scope',
      title: '1. Champ d\'application',
      content: 'Ces conditions s\'appliquent à l\'utilisation de l\'Application voxia, propriété de GBM Medical SA (IDE: CHE-410.905.318), sise Chemin des Bûcherons 116, 1234 Vessy, Genève, Suisse. L\'application est destinée exclusivement aux professionnels de santé autorisés en Suisse pour la transcription et structuration de dictées médicales.'
    },
    {
      id: 'eligibility',
      title: '2. Éligibilité',
      content: 'L\'accès est réservé aux médecins titulaires d\'un brevet fédéral ou diplôme reconnu par la MEBEKO, ainsi qu\'aux secrétaires médicales sous supervision d\'un médecin FMH. Tout rapport généré par une secrétaire doit être validé par le médecin superviseur.'
    },
    {
      id: 'account',
      title: '3. Compte utilisateur',
      content: 'L\'accès nécessite la création d\'un compte avec authentification renforcée (SMS + code de sécurité ou reconnaissance faciale). Vous êtes responsable de la confidentialité de vos identifiants et de toute activité liée à votre compte. Vous devez obtenir le consentement préalable de vos patients pour le traitement de leurs données.'
    },
    {
      id: 'usage',
      title: '4. Utilisation autorisée',
      content: 'L\'utilisation est strictement limitée à vos besoins professionnels internes. Vous vous engagez à respecter la législation suisse (notamment la LPD), à ne pas partager vos accès, et à fournir des dictées de qualité. Il est recommandé d\'utiliser des identifiants anonymisés pour réduire les risques.'
    },
    {
      id: 'responsibility',
      title: '5. Responsabilité médicale',
      content: 'VOUS ÊTES SEUL RESPONSABLE de la vérification, validation et exactitude des rapports générés. Voxia ne remplace pas le jugement clinique. Toute décision médicale relève exclusivement de votre responsabilité professionnelle. Nous déclinons toute responsabilité pour l\'utilisation clinique des contenus générés.'
    }
  ];

  const applicationSection = {
    title: '6. Propriété intellectuelle',
    subsections: [
      {
        title: '6.1 Droits exclusifs',
        content: 'Tous les droits de propriété intellectuelle relatifs à voxia (logiciels, algorithmes, interfaces, bases de données) sont la propriété exclusive de GBM Medical SA. Toute reproduction, modification ou extraction non autorisée est interdite.'
      },
      {
        title: '6.2 Contenu utilisateur',
        content: 'Vous conservez tous vos droits sur le contenu que vous téléchargez. Vous nous accordez une licence limitée pour traiter ce contenu aux fins de fonctionnement et d\'amélioration de voxia, dans le respect de notre Politique de confidentialité et de la LPD.'
      },
      {
        title: '6.3 Amélioration',
        content: 'Toute amélioration ou modification de voxia que vous créez est automatiquement cédée à GBM Medical SA de manière exclusive et irrévocable, pour le monde entier et pour toute la durée de protection applicable.'
      }
    ]
  };

  const responsabilitySection = {
    title: '7. Limitation de responsabilité',
    subsections: [
      {
        title: '7.1 Application fournie "en l\'état"',
        content: 'voxia est fournie "en l\'état", sans garantie de disponibilité continue, d\'exactitude ou d\'absence d\'erreur. Nous déclinons toute responsabilité pour les interruptions, pertes de données ou défauts techniques.'
      },
      {
        title: '7.2 Exclusion des dommages indirects',
        content: 'Nous déclinons toute responsabilité pour les dommages indirects, y compris perte de chiffre d\'affaires, perte de clientèle, préjudice réputationnel ou corruption de données, sauf en cas de faute intentionnelle ou négligence grave.'
      },
      {
        title: '7.3 Plafonnement',
        content: 'Notre responsabilité est plafonnée au montant total payé par vous pour voxia au cours des 12 mois précédant l\'événement donnant lieu à responsabilité.'
      }
    ]
  };

  const dataProtectionSection = {
    title: '8. Protection des données',
    subsections: [
      {
        title: '8.1 Conformité LPD',
        content: 'Le traitement des données se fait conformément à la Loi fédérale sur la protection des données (LPD), entrée en vigueur le 1er septembre 2023. Nous garantissons un traitement confidentiel et sécurisé des données personnelles et sensibles.'
      },
      {
        title: '8.2 Données traitées',
        content: 'Nous collectons : nom, prénom, numéro de téléphone, e-mail et spécialisation. Le numéro de téléphone est requis pour l\'authentification par SMS. Les fichiers audio/image sont supprimés immédiatement après traitement. Les textes générés sont conservés pour amélioration du service.'
      },
      {
        title: '8.3 Vos droits',
        content: 'Vous disposez d\'un droit d\'accès, de rectification, de suppression et d\'opposition. Pour exercer ces droits, contactez-nous via la page Contact. Les données sont stockées en Europe et ne sont jamais transmises à des tiers non autorisés.'
      }
    ]
  };

  return (
    <div className="terms-page">
      {!isAuthenticated && <HeaderNoAuth />}
      {!isAuthenticated && <BackButton to="/landing" />}
      {/* Header */}
      <header className="terms-header">
        {isAuthenticated && <BackButton to="/guide" />}
        <div className="header-content">
          <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="voxia" className="logo" />
          <h1>Conditions générales d'utilisation</h1>
          <p className="last-updated">
            Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          <p className="terms-intro">
            Résumé des principales dispositions. Pour consulter les documents contraignants complets :
          </p>
        </div>
      </header>

      {/* Content */}
      <div className="settings-container">
        {/* Legal Documents Links */}
        <Card className="card-large">
          <div className="terms-section">
            <h2>Documents complets</h2>
            <p style={{ marginBottom: '20px' }}>
              Cette page présente un résumé indicatif et non exhaustif des conditions d'utilisation.
              Pour une information complète et juridiquement contraignante, veuillez consulter les documents ci-dessous :
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={FileText}
                onClick={() => openExternalUrl('https://voxia-md.ch/public/cg/cgu.pdf')}
              >
                Conditions générales d'utilisation
              </Button>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={Scale}
                onClick={() => openExternalUrl('https://voxia-md.ch/public/cg/cgv.pdf')}
              >
                Conditions générales de vente
              </Button>
              <Button
                variant="secondary"
                type="default"
                size="medium"
                icon={Shield}
                onClick={() => openExternalUrl('https://voxia-md.ch/public/cg/dpd.pdf')}
              >
                Déclaration de protection des données 
              </Button>
            </div>
          </div>
        </Card>

        {/* Introduction sections */}
        {sections.map((section) => (
          <Card key={section.id} className="card-large">
            <div className="terms-section">
              <h2>{section.title}</h2>
              <p>{section.content}</p>
            </div>
          </Card>
        ))}

        {/* Property section with subsections */}
        <Card className="card-large">
          <SectionHeader icon={Globe} title={applicationSection.title} />
          <div className="terms-section">
            {applicationSection.subsections.map((subsection, index) => (
              <div key={index} className="terms-subsection">
                <h3>{subsection.title}</h3>
                <p>{subsection.content}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Responsibility section */}
        <Card className="card-large">
          <SectionHeader icon={Shield} title={responsabilitySection.title} />
          <div className="terms-section">
            {responsabilitySection.subsections.map((subsection, index) => (
              <div key={index} className="terms-subsection">
                <h3>{subsection.title}</h3>
                <p>{subsection.content}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Data Protection section */}
        <Card className="card-large">
          <SectionHeader icon={Database} title={dataProtectionSection.title} />
          <div className="terms-section">
            {dataProtectionSection.subsections.map((subsection, index) => (
              <div key={index} className="terms-subsection">
                <h3>{subsection.title}</h3>
                <p>{subsection.content}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Additional Important Sections */}
        <Card className="card-large">
          <SectionHeader icon={FileText} title="9. Dispositions complémentaires" />
          <div className="terms-section">
            <div className="terms-subsection">
              <h3>Modifications</h3>
              <p>
                Nous pouvons modifier ces conditions à tout moment. Les modifications substantielles vous seront notifiées par e-mail ou SMS au moins 15 jours avant leur entrée en vigueur.
              </p>
            </div>
            <div className="terms-subsection">
              <h3>Résiliation</h3>
              <p>
                Vous pouvez résilier votre compte à tout moment avec un préavis de 15 jours. Nous pouvons suspendre votre accès en cas de violation des présentes conditions.
              </p>
            </div>
            <div className="terms-subsection">
              <h3>Droit applicable</h3>
              <p>
                Ces conditions sont régies par le droit suisse. Tout litige sera soumis à arbitrage à Genève, conformément au Règlement suisse d'arbitrage international.
              </p>
            </div>
          </div>
        </Card>

        {/* Contact and Provider Info */}
        <Card className="card-large">
          <SectionHeader icon={Mail} title="10. Contact et informations légales" />
          <div className="terms-section">
            <div className="terms-subsection">
              <h3>Fournisseur</h3>
              <p>
                <strong>GBM Medical SA</strong><br />
                IDE : CHE-410.905.318<br />
                Chemin des Bûcherons 116<br />
                1234 Vessy, Genève, Suisse
              </p>
            </div>
            <div className="terms-subsection">
              <h3>Support et questions</h3>
              <p style={{ marginBottom: '16px' }}>
                Pour toute question concernant ces conditions, l'utilisation de voxia ou pour exercer vos droits :
              </p>
              <Button
                variant="primary"
                type="default"
                size="large"
                icon={Mail}
                onClick={() => navigate('/contact')}
              >
                Contactez-nous
              </Button>
            </div>
          </div>
        </Card>

        {/* Final Note */}
        <Card className="card-large">
          <div className="terms-cta" style={{ textAlign: 'center' }}>
            <p style={{ fontSize: '16px', color: '#666' }}>
              Merci d'avoir choisi voxia pour optimiser vos tâches administratives.<br />
              Nous nous engageons à fournir un service fiable et sécurisé, à la hauteur de vos attentes professionnelles.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default TermsOfService;